// Pulled from Creed, not sure we actually need all these settings

import {
  LitElement,
  html,
  classMap,
  css,
  unsafeHTML,
  t,
  paths,
} from '@hh/global.js'

class HHWistiaVideoWrapper extends LitElement {
  static properties = {
    videoId: {
      type: String,
      reflect: true,
      attribute: 'video-id',
    },
    paused: {
      type: Boolean,
      reflect: true,
    },
    muted: {
      type: Boolean,
      reflect: true,
    },
    video: {
      type: Object,
    },
    wistiaControls: {
      type: Boolean,
      reflect: true,
      attribute: 'wistia-controls',
    },
    forceVideoDimensions: {
      type: Boolean,
      attribute: 'force-video-dimensions',
    },
    showToggleButton: {
      type: Boolean,
      reflect: true,
      attribute: 'show-toggle-button',
    },
    plyrURL: {
      type: String,
      reflect: true,
      attribute: 'plyr-url',
    },
  }

  constructor() {
    super()
    window._wq = window._wq || []
    this.paused = true
    this.muted = true
    this.isMotionReduced = matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches

    this.wistiaOptions = {
      videoFoam: true,
      fitStrategy: 'cover',
      endVideoBehavior: 'loop',
      volume: 0,
      autoplay: !this.isMotionReduced,
      controlsVisibleOnLoad: this.wistiaControls,
      playSuspendedOffScreen: false,
      silentAutoplay: true,
      playPauseNotifier: false,
    }
  }

  firstUpdated() {
    this.containerHeight = this.getBoundingClientRect().height
    const videoId = this.videoId
    const plyrURL = this.plyrURL

    if (plyrURL) {
      // Wait for iframe to be in DOM
      this.updateComplete.then(() => {
        const iframe = this.querySelector('.plyr__video-embed')

        if (iframe) {
          // Load Plyr if not already loaded
          if (!window.Plyr) {
            const script = document.createElement('script')
            script.src = 'https://cdn.plyr.io/3.7.8/plyr.js'
            script.onload = () => this.initPlyr(iframe)
            document.head.appendChild(script)
          } else {
            this.initPlyr(iframe)
          }
        }
      })
      return
    }

    if (videoId && !this.plyrURL) {
      window._wq.push({
        id: videoId,
        options: this.wistiaOptions,
        onReady: (video) => {
          this.video = video
          this.initVideo()
        },
      })
    }
  }

  initPlyr(iframe) {
    this.plyr = new window.Plyr(iframe, {
      youtube: {
        noCookie: true,
        controls: 0,
        showinfo: 0,
        modestbranding: 1,
        wmode: 'transparent',
        mute: 1,
        loop: 1,
        rel: 0,
        autoplay: this.isMotionReduced ? 0 : 1,
      },
      vimeo: {
        preload: 'auto',
        byline: false,
        portrait: false,
        title: false,
        autoplay: !this.isMotionReduced,
        loop: true,
        controls: false,
        muted: true,
      },
      controls: [], // We'll use our own controls
      muted: this.muted,
    })

    this.videoReady = true

    this.plyr.on('ready', (event) => {
      if (!this.isMotionReduced && this.muted) {
        this.plyr.muted = true
        this.plyr.volume = 0

        this.play()
      }
    })

    this.plyr.on('ended', () => {
      this.plyr.currentTime = 0
      this.play()
    })
  }

  updated(changedProperties) {
    if (changedProperties.has('paused')) {
      this.paused ? this.pause() : this.play()
    }

    if (changedProperties.has('muted')) {
      if (this.plyrURL && this.plyr) {
        this.plyr.volume = this.muted ? 0 : 1
        this.plyr.muted = this.muted
      }
    }
  }

  initVideo() {
    if (!this.video) return

    if (this.forceVideoDimensions) {
      this.video.videoHeight(this.containerHeight, { constrain: true })
    }
    this.video.mute()
    this.videoReady = true
    this.paused = this.isMotionReduced
  }

  onToggleClick(e) {
    e.preventDefault()
    e.stopPropagation()
    this.paused = !this.paused
  }

  play() {
    if (this.plyrURL && this.plyr) {
      this.plyr.play()
      this.paused = false
    } else if (this.video) {
      this.video.play()
      this.paused = false
    }
  }

  pause() {
    if (this.plyrURL && this.plyr) {
      this.plyr.pause()
      this.paused = true
    } else if (this.video) {
      this.video.pause()
      this.paused = true
    }
  }

  handleVolumeToggle(e) {
    e.preventDefault()
    e.stopPropagation()

    this.muted ? this.video?.unmute() : this.video?.mute()
    this.muted = !this.muted
  }

  render() {
    return html`
      <div class="wrapper">
        <slot name="video"></slot>

        <button
          class="${classMap({
            toggle: true,
            ready: this.videoReady,
            'use-wistia-controls': this.wistiaControls,
            'hide-toggle': !this.showToggleButton,
          })}"
          @click=${this.onToggleClick}
          aria-label=${this.paused
            ? t('accessibility.play_video')
            : t('accessibility.pause_video')}
        >
          ${this.paused ? unsafeHTML(paths.play) : unsafeHTML(paths.pause)}
        </button>
        <button
          class="${classMap({
            toggle: true,
            mute: true,
            ready: this.videoReady,
            'use-wistia-controls': this.wistiaControls,
            '_hide-toggle': !this.showToggleButton,
            get 'hide-toggle'() {
              return this['_hide-toggle']
            },
            set 'hide-toggle'(value) {
              this['_hide-toggle'] = value
            },
          })}"
          @click="${this.handleVolumeToggle}"
          aria-label=${this.muted
            ? t('accessibility.sound_video')
            : t('accessibility.mute_video')}
        >
          ${this.muted ? unsafeHTML(paths.mute) : unsafeHTML(paths.sound)}
        </button>
      </div>
    `
  }

  static styles = css`
    :host *,
    :host *::before,
    :host *::after {
      box-sizing: border-box;
    }

    :host {
      display: block;
      height: 100%;
      background: var(--hh-color-foreground);
    }

    .wrapper {
      height: 100%;
      position: relative;
    }

    .toggle {
      display: none;
      position: absolute;
      bottom: 2rem;
      right: 1rem;
      width: 4rem;
      height: 4rem;
      border-radius: 50%;
      border: none;
      color: var(--hh-color-background);
      background-color: var(--hh-color-day-extra-light-opacity);
      cursor: pointer;
      padding: 0;
      fill: currentColor;
      align-items: center;
      justify-content: center;
      z-index: 11;
    }

    .toggle.mute {
      right: 5rem;
    }

    @media screen and (min-width: 750px) {
      .toggle {
        right: 2rem;
      }

      .toggle.mute {
        right: 6rem;
      }
    }

    .toggle.hide-toggle {
      display: none !important;
    }

    .toggle.use-wistia-controls {
      display: none;
    }

    .toggle.ready {
      display: flex;
    }

    .toggle:not(.mute) svg {
      fill: transparent;
      transition: fill var(--hh-duration-default) ease-in-out;
    }

    @media (hover: hover) {
      .toggle:is(:focus-visible, :hover):not(.mute) svg {
        fill: currentColor;
      }
    }
  `
}

customElements.define('hh-wistia-video-wrapper', HHWistiaVideoWrapper)
